import moment, { Moment } from "moment-timezone";
import { isTimezoneSupportEnabled } from "@/utils/timezone/timezone";

const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const UTC_ISO_DATETIME_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:?\d{2})$/;
const LEGACY_NAIVE_DATETIME_PATTERN = /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}(:\d{2})?$/;

const DEFAULT_DATETIME_FORMAT = "DD/MM/YYYY HH:mm";
const DEFAULT_DATE_FORMAT = "DD/MM/YYYY";

const warnedLegacyNaiveValues = new Set<string>();

// The member's stored timezone preference (TD-192), kept here rather than passed
// through ~100 call sites — a page that converted only some of its times would be
// worse than one that converted none. Empty means "no preference", and UTC
// instants then render in the device's own zone, as they did before TD-192.
let displayTimeZone = "";

export const setDisplayTimeZone = (timeZone: unknown): void => {
  // The value comes from an API, so an unknown identifier must not be allowed to
  // turn every converted datetime into "Invalid date".
  displayTimeZone =
    typeof timeZone === "string" && moment.tz.zone(timeZone) ? timeZone : "";
};

export const getDisplayTimeZone = (): string => displayTimeZone;

// "Now" in the same frame as anything parseApiDateTime returns. Needed for
// granularity comparisons — isBefore(other, "day") truncates each side using its
// own offset, so comparing a zoned event against a device-local now can be a day
// out. Instant comparisons (>=, isBefore with no unit) don't need this.
export const nowInDisplayZone = (): Moment =>
  displayTimeZone ? moment().tz(displayTimeZone) : moment();

export const isDateOnly = (value: string): boolean =>
  DATE_ONLY_PATTERN.test(value.trim());

export const isUtcIsoDateTime = (value: string): boolean =>
  UTC_ISO_DATETIME_PATTERN.test(value.trim());

export const isLegacyNaiveDateTime = (value: string): boolean =>
  !isUtcIsoDateTime(value) && LEGACY_NAIVE_DATETIME_PATTERN.test(value.trim());

const warnLegacyNaiveDateTime = (value: string): void => {
  // Only the Australian platform sends the header, so outside it every datetime is
  // naive by design and the warning would fire on each one with nothing to act on.
  if (!isTimezoneSupportEnabled()) return;
  if (warnedLegacyNaiveValues.has(value)) return;
  warnedLegacyNaiveValues.add(value);
  console.warn(
    `[dateTime] Received a naive datetime string with no timezone marker: "${value}". ` +
      "This endpoint has not adopted the X-Response-Timezone header yet — displaying " +
      "as local wall-clock time without conversion. Flag this endpoint to the backend team.",
  );
};

export const parseApiDateTime = (value: unknown): Moment | null => {
  if (typeof value !== "string" || value.trim() === "") return null;

  const trimmedValue = value.trim();

  if (isDateOnly(trimmedValue)) {
    const dateOnlyMoment = moment(trimmedValue, "YYYY-MM-DD", true);
    return dateOnlyMoment.isValid() ? dateOnlyMoment : null;
  }

  // Only a real instant can be re-zoned. A naive value carries no offset, so it
  // is wall-clock text and converting it would invent an hour it never had.
  if (isUtcIsoDateTime(trimmedValue)) {
    const parsedUtc = moment.utc(trimmedValue);
    const utcMoment = displayTimeZone
      ? parsedUtc.tz(displayTimeZone)
      : parsedUtc.local();
    return utcMoment.isValid() ? utcMoment : null;
  }

  if (isLegacyNaiveDateTime(trimmedValue)) {
    warnLegacyNaiveDateTime(trimmedValue);
  }

  const fallbackMoment = moment(trimmedValue);
  return fallbackMoment.isValid() ? fallbackMoment : null;
};

export const formatLocalDateTime = (
  value: unknown,
  format: string = DEFAULT_DATETIME_FORMAT,
): string => {
  const parsedMoment = parseApiDateTime(value);
  return parsedMoment ? parsedMoment.format(format) : "";
};

// parseApiDateTime for callers that may also be handed a Date, a moment, or a
// number. Strings keep parseApiDateTime's rules (instants convert, naive and
// date-only values do not); anything else is re-zoned only if it is a real instant.
export const toDisplayMoment = (value: unknown): Moment | null => {
  if (typeof value === "string") return parseApiDateTime(value);
  if (value === null || value === undefined) return null;
  const parsed = moment(value as moment.MomentInput);
  if (!parsed.isValid()) return null;
  return displayTimeZone ? parsed.tz(displayTimeZone) : parsed;
};

export const formatDateOnly = (
  value: unknown,
  format: string = DEFAULT_DATE_FORMAT,
): string => {
  if (typeof value !== "string" || value.trim() === "") return "";
  const dateOnlyMoment = moment(value.trim(), "YYYY-MM-DD", true);
  return dateOnlyMoment.isValid() ? dateOnlyMoment.format(format) : "";
};
